import { type DialogProps } from '@radix-ui/react-dialog'
import { Dialog, DialogContent } from '@repo/ui/dialog'
import { cn } from '@repo/ui/utils'
import { Command as CommandPrimitive } from 'cmdk'
import { Search } from 'lucide-react'
import * as React from 'react'
import { useEffect, useImperativeHandle } from 'react'

const Command = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive>
>(({ className, ...props }, ref) => (
  <CommandPrimitive
    ref={ref}
    className={cn(
      'text-primary-950 dark:bg-primary-950 dark:text-primary-50 flex h-full w-full flex-col overflow-hidden rounded-md',
      className,
    )}
    {...props}
  />
))
Command.displayName = CommandPrimitive.displayName

interface CommandDialogProps extends DialogProps {}

const CommandDialog = ({ children, ...props }: CommandDialogProps) => {
  return (
    <Dialog {...props}>
      <DialogContent className="overflow-hidden p-0 shadow-lg">
        <Command className="[&_[cmdk-group-heading]]:text-primary-500 dark:[&_[cmdk-group-heading]]:text-primary-400 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-group]]:px-2 [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3 [&_[cmdk-item]_svg]:h-5 [&_[cmdk-item]_svg]:w-5">
          {children}
        </Command>
      </DialogContent>
    </Dialog>
  )
}

const CommandInput = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Input>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input> & {
    inputClassName?: string
    iconClassName?: string
    hasCommandShortcut?: boolean
  }
>(
  (
    {
      className,
      inputClassName,
      iconClassName,
      hasCommandShortcut = false,
      ...props
    },
    ref,
  ) => (
    <div
      className={cn('flex items-center border-b bg-white px-3', className)}
      cmdk-input-wrapper=""
    >
      <Search
        className={cn('mr-2 size-4 shrink-0 opacity-50', iconClassName)}
      />
      <CommandPrimitive.Input
        ref={ref}
        className={cn(
          'placeholder:text-primary-500 dark:placeholder:text-primary-400 flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none disabled:cursor-not-allowed disabled:opacity-50',
          inputClassName,
        )}
        {...props}
      />
      {hasCommandShortcut && (
        <>
          <div className="bg-primary-200 mr-2 h-6 w-px" />
          <CommandShortcut className="mr-4">⌘K</CommandShortcut>
        </>
      )}
    </div>
  ),
)

CommandInput.displayName = CommandPrimitive.Input.displayName

const CommandList = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.List>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.List
    ref={ref}
    className={cn('max-h-[300px] overflow-y-auto overflow-x-hidden', className)}
    {...props}
  />
))

CommandList.displayName = CommandPrimitive.List.displayName

const CommandEmpty = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Empty>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Empty>
>((props, ref) => (
  <CommandPrimitive.Empty
    ref={ref}
    className="py-6 text-center text-sm"
    {...props}
  />
))

CommandEmpty.displayName = CommandPrimitive.Empty.displayName

const CommandGroup = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Group>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Group>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Group
    ref={ref}
    className={cn(
      'text-primary-950 dark:text-primary-50 [&_[cmdk-group-heading]]:text-primary-500 dark:[&_[cmdk-group-heading]]:text-primary-400 overflow-hidden p-1 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium',
      className,
    )}
    {...props}
  />
))

CommandGroup.displayName = CommandPrimitive.Group.displayName

const CommandSeparator = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Separator
    ref={ref}
    className={cn('bg-primary-200 dark:bg-primary-800 -mx-1 h-px', className)}
    {...props}
  />
))
CommandSeparator.displayName = CommandPrimitive.Separator.displayName

const CommandItem = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Item> & {
    onHover?: (selected: boolean) => void
  }
>(({ className, onHover, ...props }, ref) => {
  const localRef = React.useRef<HTMLDivElement>(null)
  // Allow both forwarded and local ref
  useImperativeHandle(ref, () => localRef.current as any)

  useEffect(() => {
    if (!onHover) return
    const node = localRef.current
    if (!node) return
    let prev = node.getAttribute('data-selected') === 'true'
    if (onHover) onHover(prev)
    const observer = new MutationObserver(() => {
      const selected = node.getAttribute('data-selected') === 'true'
      if (selected !== prev) {
        prev = selected
        onHover(selected)
      }
    })
    observer.observe(node, {
      attributes: true,
      attributeFilter: ['data-selected'],
    })
    return () => observer.disconnect()
  }, [onHover])
  return (
    <CommandPrimitive.Item
      ref={localRef}
      className={cn(
        "data-[selected='true']:bg-primary-100 data-[selected=true]:text-primary-900 dark:data-[selected='true']:bg-primary-800 dark:data-[selected=true]:text-primary-50 relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50",
        className,
      )}
      {...props}
    />
  )
})

CommandItem.displayName = CommandPrimitive.Item.displayName

const CommandShortcut = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => {
  return (
    <span
      className={cn(
        'text-primary-500 dark:text-primary-400 ml-auto select-none text-xs tracking-widest',
        className,
      )}
      {...props}
    />
  )
}
CommandShortcut.displayName = 'CommandShortcut'

export {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
}
